import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { TbUpload, TbDownload } from "react-icons/tb";
import { Tooltip } from "./Tooltip";
import { useToast } from "../hooks/useToast";
import { usePulseAnimation } from "../hooks/usePulseAnimation";
import { lockBodyScroll, unlockBodyScroll } from "../utils/scrollLock";
import "./SongStructureEditor.css";
import { SongStructureBarRow } from "./SongStructureBarRow";

export type TimeSignature = {
  numerator: number;
  denominator: number;
};

export type Bar = {
  id: string;
  startBar: number;
  endBar: number;
  bpm: number;
  timeSig: TimeSignature;
};

export type SongStructureEditorProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (bars: Bar[]) => void;
  loop?: boolean;
  onLoopChange?: (loop: boolean) => void;
};

export type SongStructureExport = {
  bars: Bar[];
  loop: boolean;
  countIn: boolean;
};

const generateId = () => Math.random().toString(36).slice(2, 10);

const validDenominators = [2, 4, 8, 16];

const validateBar = (bar: any): bar is Bar => {
  if (!bar || typeof bar !== "object") return false;

  const bpmValid = Number.isFinite(bar.bpm) && bar.bpm >= 30 && bar.bpm <= 300;
  const numValid =
    Number.isInteger(bar.timeSig?.numerator) &&
    bar.timeSig.numerator >= 2 &&
    bar.timeSig.numerator <= 16;
  const denValid = validDenominators.includes(bar.timeSig?.denominator);
  const startValid = Number.isInteger(bar.startBar) && bar.startBar >= 1;
  const endValid =
    Number.isInteger(bar.endBar) &&
    bar.endBar >= bar.startBar &&
    bar.endBar <= 999;

  return bpmValid && numValid && denValid && startValid && endValid;
};

const validateSongStructure = (data: any): data is SongStructureExport => {
  if (!data || typeof data !== "object") return false;

  if (!Array.isArray(data.bars) || data.bars.length === 0) return false;
  if (typeof data.loop !== "boolean") return false;
  if (typeof data.countIn !== "boolean") return false;

  // Validate all bars
  if (!data.bars.every(validateBar)) return false;

  // Validate bars are sequential
  for (let i = 0; i < data.bars.length; i++) {
    const expectedStart = i === 0 ? 1 : data.bars[i - 1].endBar + 1;
    if (data.bars[i].startBar !== expectedStart) return false;
  }

  return true;
};

export const SongStructureEditor: React.FC<SongStructureEditorProps> = ({
  isOpen,
  onCancel,
  onSave,
  loop = false,
  onLoopChange,
}) => {
  const { showSuccess, showError, showInfo } = useToast();

  // Pulse animation hooks for buttons
  const {
    pulseState: addBarPulse,
    scaleState: addBarScale,
    triggerPulse: triggerAddBarPulse,
  } = usePulseAnimation();
  const {
    pulseState: savePulse,
    scaleState: saveScale,
    triggerPulse: triggerSavePulse,
  } = usePulseAnimation();
  const {
    pulseState: importPulse,
    scaleState: importScale,
    triggerPulse: triggerImportPulse,
  } = usePulseAnimation();
  const {
    pulseState: exportPulse,
    scaleState: exportScale,
    triggerPulse: triggerExportPulse,
  } = usePulseAnimation();

  const initialBars = useMemo<Bar[]>(
    () => [
      {
        id: generateId(),
        startBar: 1,
        endBar: 1,
        bpm: 120,
        timeSig: { numerator: 4, denominator: 4 },
      },
    ],
    []
  );

  const [bars, setBars] = useState<Bar[]>(() => {
    try {
      const raw = localStorage.getItem("customSongStructure.bars");
      if (raw) {
        const parsed: Bar[] = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Sanitize any out-of-range saved values
          const sanitized = parsed.map((b) => {
            const rawNum = b?.timeSig?.numerator;
            const clampedNumerator = Number.isFinite(rawNum)
              ? Math.max(2, Math.min(16, Math.floor(rawNum as number)))
              : 4;
            return {
              ...b,
              timeSig: {
                numerator: clampedNumerator,
                denominator: b?.timeSig?.denominator ?? 4,
              },
            } as Bar;
          });
          return sanitized;
        }
      }
    } catch {}
    return initialBars;
  });

  // Track initial bars to detect changes
  const [initialBarsSnapshot, setInitialBarsSnapshot] = useState<string>(() =>
    JSON.stringify(bars)
  );

  // Match KeyboardShortcuts modal animation pattern
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [openDenominatorFor, setOpenDenominatorFor] = useState<string | null>(
    null
  );
  const [pendingEndValues, setPendingEndValues] = useState<
    Record<string, number | undefined>
  >({});
  const [flashStartIds, setFlashStartIds] = useState<string[]>([]);
  const [flashEndIds, setFlashEndIds] = useState<string[]>([]);
  const [denominatorMenuRect, setDenominatorMenuRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasSetInitialSnapshotRef = useRef<boolean>(false);

  // Update initial snapshot only when modal first opens
  useEffect(() => {
    if (isOpen && !hasSetInitialSnapshotRef.current) {
      setInitialBarsSnapshot(JSON.stringify(bars));
      hasSetInitialSnapshotRef.current = true;
    } else if (!isOpen) {
      hasSetInitialSnapshotRef.current = false;
    }
  }, [isOpen, bars]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      lockBodyScroll(); // Lock body scroll when modal opens
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      unlockBodyScroll(); // Unlock body scroll when modal closes
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Persist bars to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem("customSongStructure.bars", JSON.stringify(bars));
    } catch {}
  }, [bars]);

  // Persist loop setting
  useEffect(() => {
    try {
      localStorage.setItem("customSongStructure.loop", JSON.stringify(loop));
    } catch {}
  }, [loop]);

  // Apply settings function
  const applySettings = useCallback(() => {
    const currentBarsSnapshot = JSON.stringify(bars);
    const hasChanged = currentBarsSnapshot !== initialBarsSnapshot;

    // Calculate isValid inline to avoid temporal dead zone
    const isValid =
      bars.length > 0 &&
      bars.every((b) => {
        const bpmValid = Number.isFinite(b.bpm) && b.bpm >= 30 && b.bpm <= 300;
        const numValid =
          Number.isInteger(b.timeSig.numerator) &&
          b.timeSig.numerator >= 1 &&
          b.timeSig.numerator <= 32;
        const denValid = validDenominators.includes(b.timeSig.denominator);
        const startValid = Number.isInteger(b.startBar) && b.startBar >= 1;
        const endValid = Number.isInteger(b.endBar) && b.endBar >= b.startBar;
        return bpmValid && numValid && denValid && startValid && endValid;
      });

    if (isValid) {
      onSave(bars);
      if (hasChanged) {
        showSuccess("Song structure updated");
      }
      setInitialBarsSnapshot(currentBarsSnapshot);
    }
  }, [bars, initialBarsSnapshot, onSave, showSuccess]);

  const handleApply = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      triggerSavePulse(event);
      applySettings();
      onCancel();
    },
    [triggerSavePulse, applySettings, onCancel]
  );

  const handleClose = useCallback(() => {
    applySettings();
    onCancel();
  }, [applySettings, onCancel]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
  }, [isOpen, handleClose]);

  // Close denominator dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".time-sig-denom-container") &&
        !target.closest(".time-sig-denom-menu")
      ) {
        setOpenDenominatorFor(null);
        setDenominatorMenuRect(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle click outside modal to close
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  if (!shouldRender) return null;

  const isValid =
    bars.length > 0 &&
    bars.every((b) => {
      const bpmValid = Number.isFinite(b.bpm) && b.bpm >= 30 && b.bpm <= 300;
      const numValid =
        Number.isInteger(b.timeSig.numerator) &&
        b.timeSig.numerator >= 1 &&
        b.timeSig.numerator <= 32;
      const denValid = validDenominators.includes(b.timeSig.denominator);
      const startValid = Number.isInteger(b.startBar) && b.startBar >= 1;
      const endValid = Number.isInteger(b.endBar) && b.endBar >= b.startBar;
      return bpmValid && numValid && denValid && startValid && endValid;
    });

  const canAddBar =
    bars.length === 0 || (bars[bars.length - 1]?.endBar ?? 0) < 999;

  const updateBar = (id: string, patch: Partial<Bar>) => {
    setBars((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const updateTimeSig = (id: string, patch: Partial<TimeSignature>) => {
    setBars((prev) =>
      prev.map((b) =>
        b.id === id ? { ...b, timeSig: { ...b.timeSig, ...patch } } : b
      )
    );
  };

  const addBar = (event: React.MouseEvent<HTMLButtonElement>) => {
    triggerAddBarPulse(event);
    setBars((prev) => {
      const lastEnd =
        prev.length > 0 ? Math.max(...prev.map((b) => b.endBar)) : 0;
      const nextStart = lastEnd + 1;
      return [
        ...prev,
        {
          id: generateId(),
          startBar: nextStart,
          endBar: nextStart,
          bpm: 120,
          timeSig: { numerator: 4, denominator: 4 },
        },
      ];
    });
  };

  const removeBar = (id: string) => {
    setBars((prev) => {
      const removedIndex = prev.findIndex((b) => b.id === id);
      const remaining = prev.filter((b) => b.id !== id);
      // Preserve original lengths (at least 1)
      const lengths = remaining.map((b) =>
        Math.max(1, (b.endBar ?? b.startBar) - b.startBar + 1)
      );

      // Re-number sequentially starting from 1, preserving lengths
      let nextStart = 1;
      const next = remaining.map((b, idx) => {
        const len = lengths[idx] ?? 1;
        const startBar = nextStart;
        const endBar = startBar + len - 1;
        nextStart = endBar + 1;
        return { ...b, startBar, endBar };
      });

      // Flash start and end for all bars after the removed one
      if (removedIndex !== -1) {
        const impacted = next.slice(removedIndex);
        const ids = impacted.map((b) => b.id);
        setFlashStartIds(ids);
        setFlashEndIds(ids);
        setTimeout(() => {
          setFlashStartIds([]);
          setFlashEndIds([]);
        }, 520);
      }

      return next;
    });
  };

  const ImportIcon = TbUpload as React.ComponentType<any>;
  const ExportIcon = TbDownload as React.ComponentType<any>;

  const handleExport = (event: React.MouseEvent<HTMLButtonElement>) => {
    triggerExportPulse(event);
    try {
      // Get count-in setting from localStorage
      const countInRaw = localStorage.getItem("countInEnabled");
      const countIn = countInRaw === "true";

      const exportData: SongStructureExport = {
        bars,
        loop,
        countIn,
      };

      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const filename = `polytrome-song-structure.json`;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      showError("Failed to export song structure. Please try again.");
      console.error("Export error:", error);
    }
  };

  const handleImportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    triggerImportPulse(event);
    fileInputRef.current?.click();
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content);

        if (!validateSongStructure(data)) {
          showError(
            "Invalid song structure file. Please ensure the file contains valid BPM (30 - 300), bars (1 - 999), Loop, and Count-in settings."
          );
          return;
        }

        // Regenerate IDs for imported bars to avoid conflicts
        const importedBars = data.bars.map((bar: Bar) => ({
          ...bar,
          id: generateId(),
        }));

        setBars(importedBars);

        // Update loop setting
        if (onLoopChange) {
          onLoopChange(data.loop);
        }

        // Update count-in setting in localStorage
        localStorage.setItem("countInEnabled", String(data.countIn));

        // Dispatch event to notify other components of countIn change
        window.dispatchEvent(new Event("storage"));

        // Apply the imported structure
        onSave(importedBars);

        showSuccess("Song structure imported successfully!");
      } catch (error) {
        showError(
          "Failed to import song structure. Please check the file format."
        );
        console.error("Import error:", error);
      }
    };

    reader.readAsText(file);
    // Reset input so the same file can be imported again if needed
    event.target.value = "";
  };

  return createPortal(
    <div
      className={`song-structure-overlay ${isAnimating ? "open" : ""}`}
      role="dialog"
      aria-modal
      onClick={handleOverlayClick}
    >
      <div
        className={`song-structure-editor ${isAnimating ? "open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "18px 22px",
          }}
        >
          <div style={{ textAlign: "left", marginBottom: "8px" }}>
            <h2
              style={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "22px",
                fontWeight: 600,
                margin: 0,
              }}
            >
              Custom Song Structure
            </h2>
          </div>
        </div>

        <div
          className="modal-content"
          style={{
            padding: "18px 26px",
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
          }}
        >
          {bars.map((bar, index) => (
            <SongStructureBarRow
              key={bar.id}
              bar={bar}
              index={index}
              bars={bars}
              validDenominators={validDenominators}
              updateBar={updateBar}
              updateTimeSig={updateTimeSig}
              removeBar={removeBar}
              pendingEndValues={pendingEndValues}
              setPendingEndValues={setPendingEndValues}
              flashStartIds={flashStartIds}
              flashEndIds={flashEndIds}
              setBars={setBars}
              setFlashStartIds={setFlashStartIds}
              setFlashEndIds={setFlashEndIds}
              openDenominatorFor={openDenominatorFor}
              setOpenDenominatorFor={setOpenDenominatorFor}
              denominatorMenuRect={denominatorMenuRect}
              setDenominatorMenuRect={setDenominatorMenuRect}
            />
          ))}

          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <button
              className="add-bar-button"
              onClick={(e) => {
                if (canAddBar) {
                  addBar(e);
                } else {
                  showInfo("The maximum number of beats in a song is 999.");
                }
              }}
              disabled={!canAddBar}
              style={{
                padding: "16px 24px",
                fontSize: "16px",
                background: canAddBar
                  ? "rgba(5, 150, 105, 0.2)"
                  : "rgba(75, 85, 99, 0.3)",
                color: "var(--text-secondary)",
                border: "none",
                borderRadius: "12px",
                cursor: canAddBar ? "pointer" : "not-allowed",
                fontWeight: "500",
                backdropFilter: "blur(7px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                transform: `translateY(0) scale(${addBarScale.scale})`,
              }}
              onMouseEnter={(e) => {
                if (canAddBar) {
                  e.currentTarget.style.boxShadow =
                    "0 12px 40px rgba(0, 0, 0, 0.2)";
                  e.currentTarget.style.background = "rgba(5, 150, 105, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 8px 32px rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.background = canAddBar
                  ? "rgba(5, 150, 105, 0.2)"
                  : "rgba(75, 85, 99, 0.3)";
              }}
            >
              {/* Pulse Animation Overlay */}
              {addBarPulse.isActive && (
                <div
                  style={{
                    position: "absolute",
                    left: addBarPulse.x,
                    top: addBarPulse.y,
                    width: "0",
                    height: "0",
                    borderRadius: "50%",
                    background: "var(--button-hover-bg)",
                    transform: "translate(-50%, -50%)",
                    animation: "pulseExpand 100ms ease-out forwards",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />
              )}
              Add Bar
            </button>
          </div>
        </div>

        {/* Mobile Apply button - shown only on mobile */}
        <div className="mobile-apply-button" style={{ display: "none" }}>
          <button
            className="save-button"
            onClick={handleApply}
            disabled={!isValid}
            style={{
              padding: "16px 24px",
              fontSize: "16px",
              background: isValid
                ? "rgba(5, 150, 105, 0.2)"
                : "rgba(75, 85, 99, 0.3)",
              color: "var(--text-secondary)",
              border: "none",
              borderRadius: "12px",
              cursor: isValid ? "pointer" : "not-allowed",
              fontWeight: "500",
              backdropFilter: "blur(7px)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              transform: `translateY(0) scale(${saveScale.scale})`,
              width: "100%",
            }}
            onMouseEnter={(e) => {
              if (isValid) {
                e.currentTarget.style.boxShadow =
                  "0 12px 40px rgba(0, 0, 0, 0.2)";
                e.currentTarget.style.background = "rgba(5, 150, 105, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
              e.currentTarget.style.background = isValid
                ? "rgba(5, 150, 105, 0.2)"
                : "rgba(75, 85, 99, 0.3)";
            }}
          >
            {/* Pulse Animation Overlay */}
            {savePulse.isActive && (
              <div
                style={{
                  position: "absolute",
                  left: savePulse.x,
                  top: savePulse.y,
                  width: "0",
                  height: "0",
                  borderRadius: "50%",
                  background: "var(--button-hover-bg)",
                  transform: "translate(-50%, -50%)",
                  animation: "pulseExpand 100ms ease-out forwards",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            )}
            Apply
          </button>
        </div>

        <div
          className="button-row"
          style={{
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              style={{ display: "none" }}
            />
            <Tooltip text="Import song structure" position="top">
              <button
                className="import-export-button"
                onClick={handleImportClick}
                style={{
                  padding: "14px",
                  fontSize: "17px",
                  background: "rgba(75, 85, 99, 0.2)",
                  color: "var(--text-primary)",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                  backdropFilter: "blur(7px)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                  transform: `translateY(0) scale(${importScale.scale})`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 12px 40px rgba(0, 0, 0, 0.2)";
                  e.currentTarget.style.background = "rgba(75, 85, 99, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 8px 32px rgba(0, 0, 0, 0.1)";
                  e.currentTarget.style.background = "rgba(75, 85, 99, 0.2)";
                }}
              >
                {/* Pulse Animation Overlay */}
                {importPulse.isActive && (
                  <div
                    style={{
                      position: "absolute",
                      left: importPulse.x,
                      top: importPulse.y,
                      width: "0",
                      height: "0",
                      borderRadius: "50%",
                      background: "var(--button-hover-bg)",
                      transform: "translate(-50%, -50%)",
                      animation: "pulseExpand 100ms ease-out forwards",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                )}
                <ImportIcon style={{ fontSize: "18px" }} />
              </button>
            </Tooltip>
            <Tooltip text="Export song structure" position="top">
              <button
                className="import-export-button"
                onClick={handleExport}
                style={{
                  padding: "14px",
                  fontSize: "17px",
                  background: "rgba(75, 85, 99, 0.2)",
                  color: "var(--text-primary)",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                  backdropFilter: "blur(7px)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                  transform: `translateY(0) scale(${exportScale.scale})`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 12px 40px rgba(0, 0, 0, 0.2)";
                  e.currentTarget.style.background = "rgba(75, 85, 99, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 8px 32px rgba(0, 0, 0, 0.1)";
                  e.currentTarget.style.background = "rgba(75, 85, 99, 0.2)";
                }}
              >
                {/* Pulse Animation Overlay */}
                {exportPulse.isActive && (
                  <div
                    style={{
                      position: "absolute",
                      left: exportPulse.x,
                      top: exportPulse.y,
                      width: "0",
                      height: "0",
                      borderRadius: "50%",
                      background: "var(--button-hover-bg)",
                      transform: "translate(-50%, -50%)",
                      animation: "pulseExpand 100ms ease-out forwards",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                )}
                <ExportIcon style={{ fontSize: "18px" }} />
              </button>
            </Tooltip>
          </div>
          <button
            className="save-button"
            onClick={handleApply}
            disabled={!isValid}
            style={{
              padding: "16px 24px",
              fontSize: "16px",
              background: isValid
                ? "rgba(5, 150, 105, 0.2)"
                : "rgba(75, 85, 99, 0.3)",
              color: "var(--text-secondary)",
              border: "none",
              borderRadius: "12px",
              cursor: isValid ? "pointer" : "not-allowed",
              fontWeight: "500",
              backdropFilter: "blur(7px)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              transform: `translateY(0) scale(${saveScale.scale})`,
            }}
            onMouseEnter={(e) => {
              if (isValid) {
                e.currentTarget.style.boxShadow =
                  "0 12px 40px rgba(0, 0, 0, 0.2)";
                e.currentTarget.style.background = "rgba(5, 150, 105, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
              e.currentTarget.style.background = isValid
                ? "rgba(5, 150, 105, 0.2)"
                : "rgba(75, 85, 99, 0.3)";
            }}
          >
            {/* Pulse Animation Overlay */}
            {savePulse.isActive && (
              <div
                style={{
                  position: "absolute",
                  left: savePulse.x,
                  top: savePulse.y,
                  width: "0",
                  height: "0",
                  borderRadius: "50%",
                  background: "var(--button-hover-bg)",
                  transform: "translate(-50%, -50%)",
                  animation: "pulseExpand 100ms ease-out forwards",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            )}
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
